/**
 * Audio caching utilities using IndexedDB to store, retrieve, and manage audio blobs.
 *
 * @module utils/audioFetch
 * @author Snowfly
 * @version 1.0.0
 */
import {fetchAudioByUrl} from "../services/result.service";

/**
 * Initializes and sets up an IndexedDB database for storing audio files.
 *
 * This asynchronous function creates or opens a database named "AudioStorage" with version 1.
 * If the database is upgraded or does not exist, an object store named "audioFiles" is created
 * with auto-increment enabled.
 *
 * @async
 * @function initIndexedDb
 * @returns {Promise<IDBDatabase>} A promise that resolves to an open `IDBDatabase` instance
 * for the "AudioStorage" database, or rejects with an error if the operation fails.
 */
const initIndexedDb = async () => {
    let promise = new Promise((resolve, reject) => {
        let db = indexedDB.open('AudioStorage', 1);

        db.onerror = (event) => {
            reject(event);
        }

        db.onupgradeneeded = async function(event) {
            let db = event.target.result;
            return await db.createObjectStore(
                'audioFiles',
                {
                    autoIncrement: true
                }
            );
        }

        db.onsuccess = (event) => {
            resolve(event.target.result);
        }
    });

    return await promise;
};

/**
 * Retrieves an item from the IndexedDB object store named 'audioFiles' based on the provided key.
 *
 * This asynchronous function initializes the IndexedDB connection, performs a read-only
 * transaction on the 'audioFiles' object store, and attempts to retrieve the item associated
 * with the specified key. It resolves with the retrieved item or rejects upon error.
 *
 * @param {string | number} key - The key used to locate the item in the 'audioFiles' object store.
 * @returns {Promise<*>} A promise that resolves with the retrieved item or rejects with an error.
 * @throws {Error} If the transaction or retrieval process fails.
 */
const indexedDBGetItem = async (key) => {
    let promise = new Promise((resolve, reject) => {
        return initIndexedDb().then((db) => {
            let tx = db.transaction('audioFiles', 'readonly');
            let store = tx.objectStore('audioFiles');
            let req;

            try {
                req = store.get(key);
            } catch (error) {
                reject(error);
            }
            req.onsuccess = function(event) {
                resolve(event.target.result);
            };
            req.onerror = function(error) {
                reject(error);
            };
        })
    });

    return await promise;
}

/**
 * Asynchronously sets a key-value pair in an IndexedDB object store named "audioFiles".
 *
 * This function initializes a connection to the IndexedDB, performs a transaction in
 * "readwrite" mode on the "audioFiles" object store, and stores the provided value with
 * the specified key. If the operation is successful, the function resolves the key and
 * retrieves the associated value from the database for verification before returning it.
 *
 * If the operation fails, the function rejects with the encountered error.
 *
 * @async
 * @function
 * @param {string} key - The key to associate with the value in the IndexedDB store.
 * @param {*} value - The value to store in the IndexedDB associated with the given key.
 * @returns {Promise<*>} A promise that resolves with the value retrieved from the
 * IndexedDB using the provided key, or null if unsuccessful.
 * @throws {DOMException} Throws an error if the storage operation fails.
 */
const indexedDBSetItem = async (key, value) => {
    let promise = new Promise((resolve, reject) => {
        return initIndexedDb().then((db) => {
            let tx = db.transaction('audioFiles', 'readwrite');
            let store = tx.objectStore('audioFiles');
            try {
                let req = store.put(value, key);
                req.onsuccess = function(event) {
                    resolve(event.target.result)
                }
                req.onerror = function(error) {
                    console.error('Failed storing entry %s, value %s: %o', key, value, error);
                    reject(error);
                }
            } catch (error) {
                console.error('Failed storing entry %s, value %s: %o', key, value, error);
                reject(error);
            }
        });
    });

    let result = await promise;
    return result ? await indexedDBGetItem(result) : null;
}

/**
 * An asynchronous function to clear all entries from the 'audioFiles' object store
 * in the IndexedDB database. This function initializes the IndexedDB instance and
 * performs a 'readwrite' transaction to remove all records from the specified object store.
 *
 * @returns {Promise<void>} A promise that resolves when all entries are cleared
 * from the 'audioFiles' object store, or rejects with an error if the operation fails.
 */
const indexedDBClear = async () => {
    let promise = new Promise((resolve, reject) => {
        return initIndexedDb().then((db) => {
            let tx = db.transaction('audioFiles', 'readwrite');
            let store = tx.objectStore('audioFiles');
            let req = store.clear();

            req.onsuccess = function(event) {
                resolve(event.target.result)
            }
            req.onerror = function(error) {
                reject(error);
            }
        });
    });

    return await promise;
}

/**
 * Deletes an item from the 'audioFiles' object store in the IndexedDB database.
 *
 * This method establishes a connection to the IndexedDB using the `initIndexedDb` function,
 * starts a transaction on the 'audioFiles' object store with readwrite privileges,
 * and deletes the record associated with the specified key.
 *
 * @param {string | number} key - The key of the item to be deleted from the 'audioFiles' object store.
 * @returns {Promise<void>} A promise that resolves when the deletion operation is completed.
 * @throws {Error} Throws an error if there is an issue initializing the database or during the deletion process.
 */
const indexedDBDeleteItem = async (key) => {
    let promise = new Promise((resolve, reject) => {
        return initIndexedDb().then((db) => {
            let tx = db.transaction('audioFiles', 'readwrite');
            let store = tx.objectStore('audioFiles');
            let req = store.delete(key);

            req.onsuccess = function(event) {
                resolve(event.target.result)
            }
            req.onerror = function(error) {
                reject(error);
            }
        });
    });

    return await promise;
}

/**
 * Asynchronously fetches and stores an audio file.
 *
 * This function attempts to retrieve an audio file from the given S3 URL. If fetching from the
 * S3 URL fails, it attempts to fetch the audio from an alternative API. If both attempts fail,
 * the audio data associated with the given key is deleted. Upon successful fetching, the audio
 * file is stored as a Blob in IndexedDB via localForage.
 *
 * @param {string} audioKey - The unique key associated with the audio file.
 * @param {string} s3Url - The URL of the audio file in the S3 bucket.
 * @returns {Promise<Blob|null>} A promise that resolves to the stored audio Blob if successful,
 * or null if both fetch attempts fail.
 */
const fetchAndStoreAudio = async (audioKey, s3Url) => {
    let response;

    try {
        response = await fetch(s3Url);
        if (response?.ok) {
            const resp = new Response(response.body);
            response = await resp.arrayBuffer();
        }
        if (!response.ok) {
            response = null;
        }
    } catch (error) {
        response = null;
    }

    if (!response) {
        try {
            response = await fetchAudioByUrl(s3Url);
        } catch (error) {
            //console.error('Error fetching audio from API:', error);
        }
    }

    if (!response) {
        //console.error('Could not fetch audio from S3 or API. URL: %s, key: %s', s3Url, audioKey);
        await deleteAudio(audioKey);

        return null;
    }

    // Store the Blob in IndexedDB using localForage
    try {
        response = await indexedDBSetItem(audioKey, response);
    } catch (error) {
        //console.error('Error storing audio in IndexedDB:', error);
    }

    return response;
};

/**
 * Asynchronously retrieves an audio file based on the provided key. If the audio file
 * is not found in IndexedDB, it is fetched from the specified URL and subsequently
 * stored in IndexedDB for future use. The function returns a local object URL for
 * playback of the audio.
 *
 * @param {string} audioKey - The key used to retrieve the audio file from IndexedDB.
 * @param {string} audioUrl - The URL to fetch the audio file from if it is not found in IndexedDB.
 * @returns {Promise<string|null>} A Promise that resolves to a string containing a blob URL for the
 *                                  audio file, or null if retrieval and fetching both fail.
 * @throws Will log an error to the console if there is an issue with fetching audio from IndexedDB.
 */
const getAudio = async (audioKey, audioUrl) => {
    let audioBlob = null;

    try {
        audioBlob = await indexedDBGetItem(audioKey);
    } catch (error) {
        //console.error('Error fetching audio from IndexedDB:', error);
    }

    if (!audioBlob) {
        if (!audioUrl) {
            //console.error('NULL audioUrl passed to getAudio');
            return null;
        }
        // If not found, fetch it from S3 and store it
        audioBlob = await fetchAndStoreAudio(audioKey, audioUrl);
    }

    // Create a local object URL for playback
    return audioBlob ?
        URL.createObjectURL(
            new Blob([audioBlob], { type: 'audio/mpeg' })
        ) :
        null;
}

/**
 * Releases the memory associated with an audio object by revoking its object URL.
 * This helps to manage browser resources by freeing up memory allocated for the audio object.
 *
 * @param {string} audioObj - The object URL of the audio resource to be revoked.
 * @returns {void} A promise that resolves once the object URL is successfully revoked.
 */
const freeAudio = async (audioObj) => {
    return URL.revokeObjectURL(audioObj);
}

/**
 * Deletes an audio record from the IndexedDB storage.
 *
 * @async
 * @function deleteAudio
 * @param {string} audioKey - The unique key identifying the audio record to be deleted.
 * @returns {Promise<void>} A promise that resolves when the audio record is successfully deleted.
 */
const deleteAudio = async (audioKey) => {
    return await indexedDBDeleteItem(audioKey);
}

/**
 * Asynchronously clears all audio data stored in the IndexedDB.
 *
 * This function interacts with the IndexedDB storage and ensures
 * that all audio-related data is removed. Primarily used for cleanup
 * or resetting the audio storage state within the application.
 *
 * @async
 * @function
 * @returns {Promise<void>} A promise that resolves once the IndexedDB storage has been successfully cleared.
 */
const clearAudioStorage = async () => {
    return await indexedDBClear();
}

export {fetchAndStoreAudio, getAudio, freeAudio, deleteAudio, clearAudioStorage};
